import React, { forwardRef, ReactNode, useContext, useRef } from 'react';
import { DATA_HOOKS, ALIGN, PADDING } from './TableListItem.constants';
import { st, classes } from './TableListItem.st.css';
import { Checkbox } from '../Checkbox';
import { DragHandle } from '../DragHandle/DragHandle';
import { forkRef } from '../utils/forkRef';
import { useDraggableContainer } from '../DraggableContainer/useDraggableContainer';
import { CaretRight } from '@wix/wix-ui-icons-common/system';
import { TableListItemColumn, TableListItemProps } from './TableListItem.types';
import { WixDesignSystemContext } from '../WixDesignSystemProvider/context';
import { useIcons } from '../WixDesignSystemIconThemeProvider';
import { useDefaultProps } from '../WixDesignSystemDefaultPropsProvider';

const getWidthStyle = (options: TableListItemColumn[]) =>
  options.reduce(
    (acc, { width }) =>
      `${acc} ${typeof width === 'number' ? width + 'px' : width || '1fr'}`,
    '',
  );

const TableListItem = forwardRef((props: TableListItemProps, ref) => {
  const Icons = useIcons('TableListItem', {
    CaretRight,
  });
  const {
    options,
    verticalPadding = PADDING.small,
    horizontalPadding = PADDING.large,
    selectable = false,
    checkbox = false,
    checkboxDisabled,
    checkboxTooltipContent,
    checkboxTooltipProps,
    checked,
    onCheckboxChange = () => {},
    focused,
    onBlur,
    onFocus,
    showDivider = false,
    onKeyUp,
    onClick,
    onContextMenu,
    onMouseEnter,
    onMouseLeave,
    className,
    dataHook,
    draggable = false,
    dragging,
    dragDisabled,
    dragHandleProps,
    hideDragHandle,
    dragHandleSize = 'large',
    showDragHandleOnHover = false,
    border = false,
    borderRadius,
    showSelectionBorder = true,
    expandable = false,
    expandDisabled,
    reserveExpandHandleSpace,
    reserveDragHandleSpace,
    onClickExpand,
    expanded = false,
    ariaLabel,
    contentStyle = {},
  } = useDefaultProps('TableListItem', props);

  const { mobile } = useContext(WixDesignSystemContext);
  const draggableRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const hasControls =
    draggable ||
    expandable ||
    checkbox ||
    reserveExpandHandleSpace ||
    reserveDragHandleSpace;
  const isSelectableWithoutCheckbox = !!(selectable && onClick && !checkbox);
  const noVisibleControls =
    hideDragHandle &&
    !expandable &&
    !checkbox &&
    !reserveExpandHandleSpace &&
    !reserveDragHandleSpace;

  React.useEffect(() => {
    if (draggableRef.current && focused) {
      draggableRef.current.focus();
    }
  }, [draggableRef, focused]);

  React.useImperativeHandle(ref, () => ({
    focus: () => rootRef.current && rootRef.current.focus(),
  }));

  const { className: draggableContainerClassName } = useDraggableContainer({
    dragging,
    draggable,
    dragDisabled,
    highlight: true,
  });

  function selectItem(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' && document.activeElement === rootRef?.current) {
      onClick?.(e);
    }
  }

  function interceptOnClickExpand(e: React.MouseEvent<HTMLDivElement>) {
    onClickExpand?.(e);
    e.stopPropagation();
  }

  const ExpandHandle = ({ children }: { children: ReactNode }) => {
    if (expandable) {
      return (
        <div
          className={st(classes.expandHandle, {
            expandable: expandable && !expandDisabled,
          })}
          onClick={expandDisabled ? undefined : interceptOnClickExpand}
          data-hook={DATA_HOOKS.tableListItemExpandHandleContainer}
        >
          {children}
        </div>
      );
    }

    if (reserveExpandHandleSpace) {
      return (
        <div
          className={st(classes.expandHandle, {
            hidden: reserveExpandHandleSpace,
          })}
        >
          {children}
        </div>
      );
    }

    return null;
  };

  return (
    <div
      ref={rootRef}
      data-hook={dataHook}
      className={st(
        classes.root,
        {
          draggable: draggable && !dragDisabled,
          dragDisabled,
          dragging,
          showDragHandleOnHover,
          checked: (checkbox || selectable) && checked,
          showDivider,
          clickable: !!onClick,
          selectable: isSelectableWithoutCheckbox,
          verticalPadding,
          horizontalPadding,
          border,
          borderRadius,
          showSelectionBorder,
          dragHandleSize,
          hideDragHandle,
          noVisibleControls,
          mobile,
        },
        className,
        draggableContainerClassName,
      )}
      tabIndex={!isSelectableWithoutCheckbox ? -1 : 0}
      aria-label={!isSelectableWithoutCheckbox ? undefined : ariaLabel}
      aria-selected={!isSelectableWithoutCheckbox ? undefined : checked}
      onKeyDown={!isSelectableWithoutCheckbox ? undefined : e => selectItem(e)}
      onFocus={onFocus}
      onBlur={onBlur}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      onContextMenu={onContextMenu}
    >
      {hasControls && (
        <div
          style={contentStyle}
          className={st(classes.controlsContainer, { dragHandleSize })}
        >
          {draggable ? (
            <div className={classes.handleContainer}>
              <DragHandle
                tabIndex={onKeyUp ? 0 : undefined}
                {...dragHandleProps}
                domRef={forkRef(draggableRef, dragHandleProps?.domRef || null)}
                onBlur={onBlur}
                onKeyUp={onKeyUp}
                dragging={dragging}
                disabled={dragDisabled}
                dataHook={DATA_HOOKS.tableListItemDragHandle}
                dragHandleSize={dragHandleSize}
              />
            </div>
          ) : reserveDragHandleSpace ? (
            <div
              className={st(classes.handleContainer, {
                hidden: reserveDragHandleSpace,
              })}
            >
              <DragHandle dragHandleSize={dragHandleSize} />
            </div>
          ) : null}

          <ExpandHandle>
            <Icons.CaretRight className={st(classes.caretIcon, { expanded })} />
          </ExpandHandle>

          {checkbox && (
            <div
              className={st(classes.checkbox, { checkboxDisabled })}
              data-hook={DATA_HOOKS.tableListItemCheckboxContainer}
            >
              <Checkbox
                checked={checked}
                disabled={checkboxDisabled}
                onChange={checkboxDisabled ? undefined : onCheckboxChange}
                dataHook={DATA_HOOKS.tableListItemCheckbox}
                tooltipContent={checkboxTooltipContent}
                tooltipProps={checkboxTooltipProps}
              />
            </div>
          )}
        </div>
      )}
      <div
        className={classes.optionsContainer}
        style={{
          gridTemplateColumns: getWidthStyle(options),
          ...(hasControls ? {} : contentStyle),
        }}
        data-hook={DATA_HOOKS.tableListItemOptionsContainer}
      >
        {options.map(({ value, align }, index) => (
          <div
            className={st(classes.align, {
              position: align && ALIGN[align],
            })}
            key={index}
            data-hook={DATA_HOOKS.tableListItemValue}
          >
            {value}
          </div>
        ))}
      </div>
    </div>
  );
});

TableListItem.displayName = 'TableListItem';

export { TableListItem };
